import { BigNumber } from 'ethers'

export enum SessionType {
  SINGLE = 'single',
  LIFETIME = 'lifetime'
}

export enum LicenseType {
  MGA_REGULAR = 'mga_regular', // MGA
  MGA_LIGHT = 'mga_light', // ggNODE
  MGA_INODE = 'mga_inode' // iNODE
}

export interface License {
  licenseId: string // Unique UUID
  /** On-chain identity, present once the licence exists as an NFT. */
  tokenId?: string
  nftContract?: string
  walletAddress: string // Wallet address of assigned user
  status: LicenseStatus // Current status of the license
  purchasedAt: Date // When the licence was created
  /**
   * When the CURRENT holder acquired it. Absent on a licence that has never
   * changed hands — read it as `purchasedAt` in that case, which is what it
   * means: bought new, the two dates are the same day.
   */
  acquiredAt?: Date
  type: LicenseType // Type of the license
  version: string // Version of the license
  name?: string // License name is set by the owner; not required
  conflicted?: boolean
  launchedAt?: Date
  lastWorkingIpAddress?: string
  activeStake?: string
  claimable?: boolean
  /**
   * This licence's Terminal, in the three states the licence table shows.
   *
   * Derived by the licences service, not assembled here: `bound` and `online`
   * are different questions, and the second one is a judgement about how stale
   * a check-in may be before it stops meaning anything — a rule that belongs in
   * one place rather than in every client that draws a dot.
   *
   * Absent on a licence type that cannot run Terminal at all.
   */
  terminal?: LicenseTerminalState
}

export interface LicenseTerminalState {
  /** A Terminal is attached. Says nothing about whether it is running. */
  bound: boolean
  /** Bound AND checked in recently enough to count as running. */
  online: boolean
  /** When the bound Terminal last checked in, if it ever has. */
  seenAt?: Date
  /** When the binding was made. */
  boundAt?: Date
}

export enum LicenseStatus {
  INITIAL = 'initial',
  OK = 'ok',
  SUSPENDED = 'suspended',
  TERMINATED = 'terminated',
  CONFLICT = 'conflict',
  PAUSED = 'paused'
}

export interface CurrentCycleDataResponse {
  currentCycleDuration: number
  license: {
    licenseId: string
    walletAddress: string
    status: string
    purchasedAt: Date
    version: string
    type: LicenseType
    name?: string
  }
}

export interface NodeConfig {
  currentUnitPrice: BigNumber
  currentUnitETHPrice: BigNumber
  maxUnitsPerWallet: BigNumber
  currentLevelUnitsSold: BigNumber
  unitsPerLevel: BigNumber
  levelUnitPriceIncrease?: BigNumber
}

export interface PromoData {
  promoId: BigNumber
  promoUnits: BigNumber
  startTimestamp: BigNumber
  endTimestamp: BigNumber
  maxPromoUnitsPerWallet: BigNumber
  promoETHPricePerUnit: BigNumber
  promoPricePerUnit: BigNumber
  ggNodeBonusPerUnit: BigNumber
  promoUnitsDistributed: BigNumber
  iNodeBonusPerUnit?: BigNumber
}

export interface BuyNodePayload {
  onBehalfOf: string
  nodeType: LicenseType
  amount: BigNumber
  referrer: string
  chainId: number
  promoCode?: string
}
export interface LogActivityData {
  indexLogActivityId: string
  license: {
    licenseId: string
    name: string
  }
  ipAddress: string
  startTime: number
  endTime?: number
  duration: number
  credits: number
  reward: number
  instanceId: string
}

interface EligibleTimeForReward {
  [LicenseType.MGA_LIGHT]: number
  [LicenseType.MGA_REGULAR]: number
  [LicenseType.MGA_INODE]: number
}

export interface CycleConfig {
  cycleStartTimestamp: number
  cycleDuration: number
  utcStartHour: number
  eligibleTimeForReward: EligibleTimeForReward
  minClaimAmount: number
}

export interface InstanceLicensesSession {
  totalDuration: number
}

export interface LicenseWithCurrentActiveTimeSession {
  activeTime: number
  license: License
}

export interface LicenseSessions {
  count: number
  sessions: {
    endTime: number
    startTime: number
    ipAddress: string
  }[]
}

export interface CycleLogActivityData {
  cycleId: string
  cycleStartTimestamp: number
  cycleEndTimestamp: number
  aggregatedWorkingTime: number
  nodesCredits: number
  boosterCredits: number
  /** Reward attributable to base credits (nodesCredits × dailyRewardPerCredit). */
  nodesReward?: number
  /** Reward attributable to upline booster credits (boosterCredits × dailyRewardPerCredit). */
  boosterReward?: number
  /** Total reward for the cycle (nodesReward + boosterReward). */
  reward: number
}

export interface CycleLogActivityResponse {
  count: number
  data: CycleLogActivityData[]
}

export interface NodesByCycleIdData {
  license: {
    licenseId: string
    type: LicenseType
    name?: string
  }
  workingTime: number
  credits: number
  /** Booster credits granted to this license through the cycle (upline-derived). */
  boosterCredits?: number
  /** Base reward for this license (credits only). */
  reward: number
  /** Booster reward for this license (boosterCredits × dailyRewardPerCredit). */
  boosterReward?: number
  /** Total reward for this license including booster. */
  totalReward?: number
}

export interface NodesByCycleIdResponse {
  cycleStartTimestamp: number
  cycleEndTimestamp: number
  data: NodesByCycleIdData[]
}

/** Per-license unclaimed reward breakdown for the connected wallet. */
export interface ClaimableByLicenseRow {
  licenseId: string
  name: string | null
  type: LicenseType | string
  status: string
  unclaimedBaseReward: number
  unclaimedBoosterReward: number
  unclaimedTotalReward: number
  unclaimedRowCount: number
}

/** One mint: when reward was credited to the contract, and for which licences. */
export interface RewardActivityMint {
  txHash: string
  timestamp: number
  totalReward: number
  rowCount: number
  failed: boolean
  licenses: { licenseId: string; name: string | null; reward: number }[]
}

/** Reward earned by a licence and not yet credited to the contract. */
export interface RewardActivityUnclaimed {
  licenseId: string
  name: string | null
  type: string
  unclaimedReward: number
  rowCount: number
  firstCycleAt: number | null
  lastCycleAt: number | null
}

export interface RewardActivityResponse {
  unclaimed: RewardActivityUnclaimed[]
  totalUnclaimed: number
  mints: RewardActivityMint[]
  totalMinted: number
  /** Accrual data begins here; nothing earlier survived the database loss. */
  accrualDataFrom: number | null
}

export interface ClaimableByLicenseResponse {
  totalUnclaimedBase: number
  totalUnclaimedBooster: number
  totalUnclaimed: number
  licenses: ClaimableByLicenseRow[]
}

export interface SessionLogInCycleByNodeIdData {
  sessionId: string
  startTime: number
  endTime: number
  ipAddress: string
  instanceId: string
}

export interface LicenseInSessionLogData {
  licenseId: string
  name?: string
}

export interface SessionLogInCycleByNodeIdResponse {
  cycle: {
    cycleStartTimestamp: number
    cycleEndTimestamp: number
  }
  count: number
  data: SessionLogInCycleByNodeIdData[]
  license: LicenseInSessionLogData
}
